import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { addDays, addMonths, differenceInCalendarDays, eachDayOfInterval, endOfMonth, format, isToday, parseISO, startOfMonth, subDays, subMonths } from "date-fns";
import { nn } from "date-fns/locale";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toISODate, floorLabel, capitalize } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CalBar } from "./Bar";
import { WHOLE, dragStore, isoWeek, minToTime, recurringItems, toMin, weekDays, weekOf, weekdayIndex } from "./engine";

const DAY_START = 7 * 60;
const DAY_END = 24 * 60;
const DAY_MIN = DAY_END - DAY_START; // 17 visible hours per day
const BAR_H = 26;
const LANE_H = 30;
const TIER1_H = 26;

/** Zoom levels: px per day, hour tick interval, snapping (minutes). */
export const ZOOMS = {
  day: { pxPerDay: 1700, hourTick: 1, snap: 15, label: "Dag", chunk: 3, max: 21 },
  week: { pxPerDay: 238, hourTick: 3, snap: 30, label: "Veke", chunk: 14, max: 98 },
  month: { pxPerDay: 46, hourTick: 0, snap: DAY_MIN, label: "Månad", chunk: 31, max: 220 },
};

/** The period a zoom level «means» – used by the parent for step navigation. */
export const timelineDays = (anchor, zoom) => (zoom === "day" ? [anchor] : zoom === "week" ? weekDays(anchor) : eachDayOfInterval({ start: startOfMonth(anchor), end: endOfMonth(anchor) }));

/** Initial window around an anchor. The window then grows in both directions as you scroll. */
const buildWindow = (anchor, zoom) => {
  if (zoom === "day") return eachDayOfInterval({ start: subDays(anchor, 3), end: addDays(anchor, 3) });
  if (zoom === "week") { const mon = weekOf(anchor); return eachDayOfInterval({ start: subDays(mon, 14), end: addDays(mon, 20) }); }
  return eachDayOfInterval({ start: startOfMonth(subMonths(anchor, 1)), end: endOfMonth(addMonths(anchor, 1)) });
};

/** Pack bars into lanes so overlapping intervals never share a lane. */
const packLanes = (bars) => {
  const sorted = [...bars].sort((a, b) => a.x - b.x || b.w - a.w);
  const laneEnds = [];
  for (const b of sorted) {
    let lane = 0;
    while (laneEnds[lane] !== undefined && laneEnds[lane] > b.x + 1) lane += 1;
    b.lane = lane;
    laneEnds[lane] = b.x + b.w;
  }
  return { bars: sorted, lanes: Math.max(1, laneEnds.length) };
};

/** Consecutive runs of days that share a month → one tier-1 header span each. */
const monthSpans = (days) => {
  const out = [];
  days.forEach((d, i) => {
    const key = format(d, "yyyy-MM");
    const last = out[out.length - 1];
    if (last && last.key === key) last.len += 1;
    else out.push({ key, start: i, len: 1, label: capitalize(format(d, "MMMM yyyy", { locale: nn })) });
  });
  return out;
};

/**
 * Resource timeline («Gantt»): one row per room, a continuous, endlessly scrolling time axis across the top.
 * The axis grows in both directions as you scroll; the parent is told which date is in focus so the period
 * label and URL can follow along. Bars sit exactly where the booking is in time. Drag to another time and/or
 * room; click empty space to create.
 */
export const TimelineView = ({ anchor, zoom = "week", rooms = [], items, recurring = [], colours, onOpen, onCreate, onMove, onVisible, compact = false }) => {
  const scroller = useRef(null);
  const hoverLine = useRef(null);
  const hoverChip = useRef(null);
  const [overRow, setOverRow] = useState(null);
  const [dropGhost, setDropGhost] = useState(null); // { roomId, x, w, label }
  const { pxPerDay, hourTick, snap, chunk, max } = ZOOMS[zoom] || ZOOMS.week;
  const pxPerMin = pxPerDay / DAY_MIN;
  const LABEL_W = compact ? 124 : 184;
  const anchorISO = toISODate(anchor);

  /* ---------- the sliding window of days ---------- */
  const [days, setDays] = useState(() => buildWindow(anchor, zoom));
  const daysRef = useRef(days); daysRef.current = days;
  const totalW = days.length * pxPerDay;
  const firstISO = toISODate(days[0]);
  const lastISO = toISODate(days[days.length - 1]);

  const dayIndex = (iso) => differenceInCalendarDays(parseISO(iso), days[0]);
  const xOf = (iso, min) => { const m = Math.min(DAY_END, Math.max(DAY_START, min)); return (dayIndex(iso) * DAY_MIN + (m - DAY_START)) * pxPerMin; };
  const geom = (it) => {
    const sMin = toMin(it.start_time) ?? DAY_START;
    const eMin = it.end_time ? toMin(it.end_time) : DAY_END;
    let x1 = Math.max(0, xOf(it.start_date, sMin));
    let x2 = Math.min(totalW, xOf(it.end_date || it.start_date, Math.max(eMin, it.end_time ? sMin + 30 : DAY_END)));
    if (x2 - x1 < 16) x2 = x1 + 16;
    return { x: x1, w: x2 - x1 };
  };
  const fromX = (x) => {
    const di = Math.min(days.length - 1, Math.max(0, Math.floor(x / pxPerDay)));
    let min = DAY_START + (x - di * pxPerDay) / pxPerMin;
    min = Math.round(min / snap) * snap;
    min = Math.min(DAY_END - 30, Math.max(DAY_START, min));
    return { iso: toISODate(days[di]), min };
  };

  /* ---------- «no» ---------- */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  const todayISO = toISODate(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const todayInWindow = todayISO >= firstISO && todayISO <= lastISO;
  const showNow = todayInWindow && nowMin >= DAY_START && nowMin <= DAY_END;
  const nowX = showNow ? xOf(todayISO, nowMin) : null;

  /** Where to put the left edge for a date: today → just before «no»; week → Monday; month → the 1st. */
  const targetX = (iso) => {
    if (iso === todayISO && zoom !== "month") return Math.max(0, xOf(iso, Math.max(DAY_START, nowMin - 90)) - 24);
    const d = parseISO(iso);
    if (zoom === "month") return xOf(toISODate(startOfMonth(d)), DAY_START);
    if (zoom === "week") return xOf(toISODate(weekOf(d)), DAY_START);
    return xOf(iso, DAY_START);
  };

  /* ---------- scroll bookkeeping ---------- */
  const shiftRef = useRef(0);            // px to add to scrollLeft after a prepend/trim
  const pendingScroll = useRef(null);    // { iso } → jump here once the new window is painted
  const reported = useRef(anchorISO);    // last focus date we told the parent about
  const programmatic = useRef(false);    // true while we animate a scroll ourselves
  const settleTimer = useRef(null);
  const raf = useRef(0);
  const extending = useRef(false);
  const [todayDir, setTodayDir] = useState(null);

  const report = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const ds = daysRef.current;
    const visW = Math.max(1, el.clientWidth - LABEL_W);
    const sl = el.scrollLeft;
    const idx = (x) => Math.min(ds.length - 1, Math.max(0, Math.floor(x / pxPerDay)));
    const focusISO = toISODate(ds[idx(sl + Math.min(visW / 3, pxPerDay * 2))]);
    const fromISO = toISODate(ds[idx(sl)]);
    const toISO = toISODate(ds[idx(sl + visW - 1)]);
    // Is today in view? If not, which way is it?
    let dir = null;
    const tISO = toISODate(new Date());
    if (tISO < fromISO) dir = "left"; else if (tISO > toISO) dir = "right";
    setTodayDir((d) => (d === dir ? d : dir));
    reported.current = focusISO;
    onVisible?.({ focus: focusISO, from: fromISO, to: toISO, programmatic: programmatic.current });
  }, [LABEL_W, pxPerDay, onVisible]);

  const extend = (dir) => {
    if (extending.current) return;
    extending.current = true;
    const prev = daysRef.current;
    let next;
    if (dir < 0) {
      const first = prev[0];
      next = [...eachDayOfInterval({ start: subDays(first, chunk), end: subDays(first, 1) }), ...prev];
      if (next.length > max) next = next.slice(0, max);
      shiftRef.current += chunk * pxPerDay;
    } else {
      const last = prev[prev.length - 1];
      next = [...prev, ...eachDayOfInterval({ start: addDays(last, 1), end: addDays(last, chunk) })];
      if (next.length > max) { const drop = next.length - max; next = next.slice(drop); shiftRef.current -= drop * pxPerDay; }
    }
    setDays(next);
  };

  const onScroll = () => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const el = scroller.current;
      if (!el) return;
      const edge = Math.max(420, pxPerDay * 1.5);
      const maxLeft = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft < edge) extend(-1);
      else if (el.scrollLeft > maxLeft - edge) extend(1);
      report();
      if (programmatic.current) {
        clearTimeout(settleTimer.current);
        settleTimer.current = setTimeout(() => { programmatic.current = false; report(); }, 160);
      }
    });
  };
  useEffect(() => () => { cancelAnimationFrame(raf.current); clearTimeout(settleTimer.current); }, []);

  const scrollToISO = (iso, behavior = "smooth") => {
    const el = scroller.current;
    if (!el) return;
    programmatic.current = behavior === "smooth";
    el.scrollTo({ left: targetX(iso), behavior });
    if (behavior !== "smooth") report();
    else { clearTimeout(settleTimer.current); settleTimer.current = setTimeout(() => { programmatic.current = false; report(); }, 400); }
  };
  const goTo = (iso) => {
    if (iso >= firstISO && iso <= lastISO) { reported.current = iso; scrollToISO(iso, "smooth"); }
    else { pendingScroll.current = { iso }; reported.current = iso; setDays(buildWindow(parseISO(iso), zoom)); }
  };

  // After the window changes: either jump to the requested date or keep the view steady after a prepend/trim.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (pendingScroll.current) {
      const { iso } = pendingScroll.current; pendingScroll.current = null; shiftRef.current = 0;
      programmatic.current = false;
      el.scrollLeft = targetX(iso);
      report();
    } else if (shiftRef.current) {
      el.scrollLeft += shiftRef.current; shiftRef.current = 0;
    }
    extending.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  // First paint: land on the anchor.
  useLayoutEffect(() => { const el = scroller.current; if (el) { el.scrollLeft = targetX(anchorISO); report(); } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zoom change: rebuild the window around whatever is in focus right now.
  const zoomRef = useRef(zoom);
  useLayoutEffect(() => {
    if (zoomRef.current === zoom) return;
    zoomRef.current = zoom;
    pendingScroll.current = { iso: reported.current };
    setDays(buildWindow(parseISO(reported.current), zoom));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  // Anchor changed from outside (prev/next/today buttons, keyboard, deep link) → go there.
  useEffect(() => {
    if (anchorISO === reported.current) return;
    goTo(anchorISO);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorISO]);

  /* ---------- rows ---------- */
  const list = useMemo(() => [...rooms].filter((r) => r.id !== WHOLE).sort((a, b) => (a.order ?? 99) - (b.order ?? 99)), [rooms]);
  const wholeRoom = rooms.find((r) => r.id === WHOLE) || { id: WHOLE, name: "Heile huset", short_name: "Heile huset" };
  const inRange = (it) => it.start_date <= lastISO && (it.end_date || it.start_date) >= firstISO;
  const rec = useMemo(() => recurringItems(recurring, firstISO, lastISO, Object.fromEntries(rooms.map((r) => [r.id, r.short_name || r.name]))), [recurring, firstISO, lastISO, rooms]);
  const visible = useMemo(() => [...items.filter(inRange), ...rec], // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, rec, firstISO, lastISO]);
  const wholeItems = visible.filter((it) => it.room_ids?.includes(WHOLE));

  const rowsData = useMemo(() => {
    const build = (room, own, ghosts) => {
      const ghostBars = ghosts.map((it) => ({ ...geom(it), item: it, ghost: true }));
      const ownBars = own.map((it) => ({ ...geom(it), item: it }));
      const g = packLanes(ghostBars);
      const o = packLanes(ownBars);
      const offset = ghostBars.length ? g.lanes : 0;
      const bars = [...g.bars, ...o.bars.map((b) => ({ ...b, lane: b.lane + offset }))];
      const lanes = offset + (ownBars.length ? o.lanes : 1);
      return { room, bars, height: Math.max(52, lanes * LANE_H + 12) };
    };
    const out = [build(wholeRoom, wholeItems, [])];
    for (const room of list) out.push(build(room, visible.filter((it) => it.room_ids?.includes(room.id)), wholeItems.filter((it) => it.kind === "entry")));
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, list, zoom, firstISO, lastISO]);

  const hours = hourTick ? Array.from({ length: DAY_MIN / 60 }, (_, i) => DAY_START / 60 + i).filter((h) => (h - DAY_START / 60) % hourTick === 0) : [];
  const tier2H = hourTick ? 54 : 40;
  const headH = TIER1_H + tier2H;
  const spans = useMemo(() => monthSpans(days), [days]);


  /* ---------- hover crosshair + drag ghost ---------- */
  const labelAt = (x) => { const { iso, min } = fromX(x); return zoom === "month" ? format(parseISO(iso), "d. MMM", { locale: nn }) : minToTime(min); };
  const onHover = (e) => {
    const line = hoverLine.current, chip = hoverChip.current;
    if (!line || !chip) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - LABEL_W;
    if (x < 0 || e.clientY - rect.top < headH || e.target.closest("button")) { hideHover(); return; }
    line.style.left = `${LABEL_W + x}px`; line.classList.remove("hidden");
    chip.style.left = `${x}px`; chip.classList.remove("hidden"); chip.firstChild.textContent = labelAt(x);
  };
  const hideHover = () => { hoverLine.current?.classList.add("hidden"); hoverChip.current?.classList.add("hidden"); };
  const ghostFor = (e, room) => {
    const d = dragStore.current;
    if (!d) return null;
    const rect = e.currentTarget.getBoundingClientRect();
    const { iso, min } = fromX(e.clientX - rect.left - (d.offset || 0));
    const it = d.item;
    const sMin = toMin(it.start_time), eMin = toMin(it.end_time);
    const dur = sMin !== null ? Math.max(30, (eMin ?? sMin + 120) - sMin) : null;
    const dayLen = Math.max(1, differenceInCalendarDays(parseISO(it.end_date || it.start_date), parseISO(it.start_date)) + 1);
    const timed = zoom !== "month" && sMin !== null;
    const x = timed ? xOf(iso, min) : xOf(iso, DAY_START);
    const w = timed ? Math.min(DAY_END, min + dur) * pxPerMin - min * pxPerMin : dayLen * pxPerDay;
    const label = timed ? `${format(parseISO(iso), "EEE d.", { locale: nn })} ${minToTime(min)}–${minToTime(Math.min(DAY_END, min + dur))}` : `${format(parseISO(iso), "EEE d. MMM", { locale: nn })}${dayLen > 1 ? ` · ${dayLen} dagar` : ""}`;
    return { roomId: room.id, x, w: Math.max(10, w), label };
  };
  const handleDrop = (e, room) => {
    e.preventDefault(); setOverRow(null); setDropGhost(null);
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const offset = Number(e.dataTransfer.getData("text/x-offset")) || 0;
    const rect = e.currentTarget.getBoundingClientRect();
    const { iso, min } = fromX(e.clientX - rect.left - offset);
    const it = items.find((x) => x.kind === "entry" && x.id === id);
    const payload = { id, start_date: iso, roomId: room.id === WHOLE ? undefined : room.id };
    if (zoom !== "month" && it && toMin(it.start_time) !== null) {
      const dur = (toMin(it.end_time) ?? toMin(it.start_time) + 120) - toMin(it.start_time);
      payload.start_time = minToTime(min);
      payload.end_time = minToTime(Math.min(DAY_END, min + Math.max(30, dur)));
    }
    onMove(payload);
  };
  const handleCreate = (e, room) => {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const { iso, min } = fromX(e.clientX - rect.left);
    onCreate(iso, { room_id: room.id === WHOLE ? WHOLE : room.id, start_time: zoom === "month" ? "" : minToTime(min), end_time: zoom === "month" ? "" : minToTime(Math.min(DAY_END, min + 120)) });
  };

  const dayLabel = (d) => (zoom === "month" ? format(d, "d", { locale: nn }) : zoom === "day" ? format(d, "EEEE d. MMMM", { locale: nn }) : format(d, "EEE d. MMM", { locale: nn }));

  return (
    <div className="relative overflow-hidden admin-card" data-testid="cal-timeline-view" data-zoom={zoom} data-window-from={firstISO} data-window-to={lastISO}>
      <div ref={scroller} onScroll={onScroll} className="cal-scroller relative max-h-[calc(100vh-300px)] min-h-[320px] overflow-auto overscroll-x-contain" data-testid="cal-timeline-scroller">
        <div className="relative" style={{ width: LABEL_W + totalW }} onMouseMove={onHover} onMouseLeave={hideHover}>
          {/* header – tier 1 months, tier 2 days/hours */}
          <div className="sticky top-0 z-20 flex overflow-hidden border-b border-border bg-card" style={{ height: headH }}>
            <div className="sticky left-0 z-30 flex shrink-0 flex-col justify-end border-r border-border bg-card px-3 pb-2" style={{ width: LABEL_W }}>
              <span className="t-mono-sm text-muted-foreground">Rom</span>
            </div>
            <div className="relative shrink-0" style={{ width: totalW }}>
              {/* tier 1 */}
              <div className="absolute inset-x-0 top-0 border-b border-border/70" style={{ height: TIER1_H }}>
                {spans.map((s) => (
                  <div key={s.key} className="absolute top-0 flex h-full items-center border-l border-border/70" style={{ left: s.start * pxPerDay, width: s.len * pxPerDay }} data-testid={`cal-timeline-month-${s.key}`}>
                    <span className="sticky whitespace-nowrap px-2 text-[11.5px] font-semibold tracking-[-0.005em] text-foreground" style={{ left: LABEL_W }}>{s.label}</span>
                  </div>
                ))}
              </div>
              {nowX !== null && (
                <div className="pointer-events-none absolute z-[5] flex -translate-x-1/2 items-center" style={{ left: nowX, top: TIER1_H + tier2H - 20, height: 18 }} data-testid="cal-timeline-now-chip">
                  <span className="rounded-full bg-sol px-1.5 py-0.5 text-[10px] font-semibold leading-none text-ink tabular shadow-soft">{String(now.getHours()).padStart(2, "0")}:{String(now.getMinutes()).padStart(2, "0")}</span>
                </div>
              )}
              <div ref={hoverChip} className="pointer-events-none absolute z-[4] flex hidden -translate-x-1/2 items-center" style={{ top: TIER1_H + tier2H - 20, height: 18 }} aria-hidden="true"><span className="rounded-full bg-foreground px-1.5 py-0.5 text-[10px] font-medium leading-none text-background tabular" /></div>
              {/* tier 2 */}
              {days.map((d, i) => {
                const tod = isToday(d);
                const we = weekdayIndex(d) >= 5;
                const mon = weekdayIndex(d) === 0;
                return (
                  <div key={toISODate(d)} className={cn("absolute flex flex-col border-l px-2 pt-1.5", mon ? "border-foreground/25" : "border-border", we && "cal-weekend", tod && "cal-today")} style={{ left: i * pxPerDay, width: pxPerDay, top: TIER1_H, height: tier2H }} data-testid={`cal-timeline-day-${toISODate(d)}`}>
                    <span className={cn("inline-flex w-fit max-w-full items-center gap-1.5 truncate text-[12px] font-medium", zoom === "month" && "flex-col gap-0 text-center", we && "text-muted-foreground")}>
                      {zoom === "month" && <span className="text-[9.5px] uppercase tracking-[0.1em] text-muted-foreground">{format(d, "EEEEE", { locale: nn })}</span>}
                      <span className={cn("rounded-full px-1 tabular", tod && "bg-sol text-ink")}>{dayLabel(d)}</span>
                      {zoom !== "month" && mon && <span className="t-mono-sm rounded-[4px] bg-secondary px-1 py-0.5 text-muted-foreground">V{isoWeek(d)}</span>}
                    </span>
                    {hourTick > 0 && hours.map((h) => <span key={h} className={cn("absolute bottom-1 text-[10px] text-muted-foreground tabular", h * 60 === DAY_START ? "translate-x-0 pl-0.5" : "-translate-x-1/2")} style={{ left: (h * 60 - DAY_START) * pxPerMin }}>{String(h).padStart(2, "0")}</span>)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* rows */}
          {rowsData.map(({ room, bars, height }, ri) => {
            const whole = room.id === WHOLE;
            const ci = whole ? 8 : colours[room.id] || 7;
            return (
              <div key={room.id} className={cn("relative flex border-b border-border last:border-b-0", ri === 0 && "bg-secondary/20")} style={{ height }} data-testid={`cal-timeline-row-${room.id}`}>
                <div className="sticky left-0 z-[16] flex shrink-0 items-start gap-2.5 border-r border-border bg-card px-3 py-2.5" style={{ width: LABEL_W }}>
                  <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: `hsl(var(--room-${ci}))` }} aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium leading-tight">{room.short_name || room.name}</p>
                    {!compact && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{whole ? "Sperrar alle rom" : [floorLabel(room.floor), room.capacity ? `${room.capacity} pers.` : null].filter(Boolean).join(" · ")}</p>}
                  </div>
                </div>
                <div
                  className={cn("relative shrink-0 cursor-cell", overRow === room.id && "cal-drop")}
                  style={{ width: totalW, backgroundImage: hourTick ? `repeating-linear-gradient(to right, hsl(var(--border) / 0.55) 0 1px, transparent 1px ${60 * pxPerMin * hourTick}px)` : undefined }}
                  onClick={(e) => handleCreate(e, room)}
                  onDragOver={(e) => { if (e.dataTransfer?.types?.includes("text/plain")) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overRow !== room.id) setOverRow(room.id); const g = ghostFor(e, room); if (g && (!dropGhost || dropGhost.x !== g.x || dropGhost.roomId !== g.roomId)) setDropGhost(g); } }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) { setOverRow(null); setDropGhost((g) => (g?.roomId === room.id ? null : g)); } }}
                  onDrop={(e) => handleDrop(e, room)}
                  data-testid={`cal-timeline-lane-${room.id}`}
                >
                  {days.map((d, i) => <div key={toISODate(d)} className={cn("pointer-events-none absolute inset-y-0 border-l", weekdayIndex(d) === 0 ? "border-foreground/25" : "border-border", weekdayIndex(d) >= 5 && "cal-weekend", isToday(d) && "cal-today")} style={{ left: i * pxPerDay, width: pxPerDay }} aria-hidden="true" />)}
                  {dropGhost?.roomId === room.id && (
                    <div className="pointer-events-none absolute top-1 z-[12] flex h-[calc(100%-8px)] min-w-[10px] flex-col justify-end rounded-[6px] border-2 border-dashed border-sol bg-sol/15" style={{ left: dropGhost.x + 1, width: dropGhost.w - 2 }} data-testid="cal-timeline-drop-ghost">
                      <span className="absolute -top-6 left-0 whitespace-nowrap rounded-md bg-foreground px-1.5 py-0.5 text-[10.5px] font-medium leading-none text-background tabular">{dropGhost.label}</span>
                    </div>
                  )}
                  {bars.map((b) => (
                    <div key={`${b.ghost ? "g-" : ""}${b.item.key}`} className="absolute" style={{ left: b.x + 1, width: Math.max(10, b.w - 2), top: 6 + b.lane * LANE_H, height: BAR_H }}>
                      <CalBar item={b.item} colours={colours} ghost={b.ghost} onOpen={onOpen} showTime={zoom !== "month" && b.w > 90} className={cn("h-full", b.w < 44 && "justify-center px-0 [&>span]:hidden")} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {nowX !== null && <div className="cal-now-line pointer-events-none absolute bottom-0 z-[15] w-[2px]" style={{ left: LABEL_W + nowX, top: headH }} data-testid="cal-timeline-now" />}
          {/* hover crosshair – positioned imperatively (no re-render on mousemove) */}
          <div ref={hoverLine} className="pointer-events-none absolute bottom-0 z-[14] hidden w-px bg-foreground/35" style={{ top: headH }} aria-hidden="true" />
        </div>
      </div>

      {/* «I dag» is off-screen → a soft nudge in the right direction */}
      {todayDir && (
        <button
          type="button"
          onClick={() => goTo(todayISO)}
          className={cn("absolute bottom-3 z-30 inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-card/90 px-3 text-[12.5px] font-medium text-foreground shadow-[0_6px_20px_-8px_hsl(var(--foreground)/0.35)] backdrop-blur transition-[transform,background-color] duration-150 hover:bg-card active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2", todayDir === "left" ? "left-3 md:left-[200px]" : "right-3")}
          data-testid="cal-timeline-today-nudge"
          data-dir={todayDir}
        >
          {todayDir === "left" && <ArrowLeft className="h-3.5 w-3.5" />}
          <span className="h-2 w-2 rounded-full bg-sol" aria-hidden="true" /> I dag
          {todayDir === "right" && <ArrowRight className="h-3.5 w-3.5" />}
        </button>
      )}
    </div>
  );
};
